import { useAtomValue } from 'jotai';

import {
  offlineDismissedSyncedBadgeIdsAtom,
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
} from '@/atoms/offline-recording';
import {
  SyncStatusBadge,
  useOfflineAwareSyncStatus,
} from '@/components/offline-recording/sync-status-badge';
import type { SyncBadgeStatus } from '@/components/offline-recording/sync-status-badge/sync-status-badge.types';
import { useIsOfflineRecordingEnabled } from '@/features/flags';
import type { OfflineSyncStatus } from '@/types/offline-recording';

interface OfflineSyncIndicatorProps {
  /** The row's offline sync status, or `undefined` for an ordinary session. */
  status: OfflineSyncStatus | undefined;
  /**
   * The row's session id, local or server. Used to resolve a note failure,
   * which outlives the sync queue row and so cannot travel in `status`. Nullable
   * because some list row types carry a nullable id; a missing id simply means
   * the row shows sync status alone.
   */
  sessionId?: string | null;
  /**
   * Forwarded to the badge; see SyncStatusBadge's hideLabel.
   * @default false
   */
  hideLabel?: boolean;
  'data-testid'?: string;
}

/**
 * Inline, read-only sync-status indicator for a session-list row: a compact
 * status glyph with a hover tooltip explaining the state. Renders nothing for
 * an ordinary session with no note failure, or for the `recording` state.
 * Reuses the colour-locked glyph vocabulary from the `SyncStatusBadge` config
 * so the row and the recorder surface stay consistent.
 *
 * Two sources, and the order between them is load-bearing. The sync queue is
 * authoritative while it holds a row: it describes work still in flight, which
 * is the more urgent story. A note failure only surfaces once that row is gone.
 * That is not a tie-break, it is the normal sequence — a failed note is
 * immediately followed by `upload_complete`, which marks the recording `synced`,
 * deletes the durable row in Rust, and lets the renderer sweep the queue item.
 * By the time a clinician is looking at this row, `status` is `undefined` and
 * the durable atom is the only thing left that knows.
 *
 * Gated on `useIsOfflineRecordingEnabled()`. The queue-derived statuses were
 * already covered indirectly — `offline_sync_status` is only populated by
 * `unionOfflineSessions`, which no-ops when the flag is off — but a note failure
 * is read straight from durable storage, which that data-layer gate never sees.
 * Persisted ids would otherwise keep surfacing after the flag was switched off,
 * including a mid-session kill switch, so the gate belongs here.
 *
 * A dismissed failure stays hidden, matching the banner's permanent per-session
 * mute. The row is the durable half of the same signal, so the two must not
 * disagree about whether a session has been acknowledged. This subtraction goes
 * away with the banner and its dismissal.
 *
 * The glyph carries an `aria-label` (its inner SVG is otherwise `aria-hidden`)
 * because, unlike the detail-header badge, there is no adjacent visible label —
 * the meaning lives only in the tooltip.
 */
export const OfflineSyncIndicator = ({
  status,
  sessionId,
  hideLabel = false,
  'data-testid': dataTestId,
}: OfflineSyncIndicatorProps) => {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const noteFailedSessionIds = useAtomValue(offlineNoteFailedSessionIdsAtom);
  const dismissedSessionIds = useAtomValue(offlineNoteFailedDismissedSessionIdsAtom);
  const dismissedSyncedIds = useAtomValue(offlineDismissedSyncedBadgeIdsAtom);

  const hasUndismissedNoteFailure =
    Boolean(sessionId) &&
    noteFailedSessionIds.includes(sessionId!) &&
    !dismissedSessionIds.includes(sessionId!);

  // An in-flight status is read as `pending_sync` while the app is offline, so
  // a row cannot spin "Syncing…" underneath the offline banner. The
  // note-failure fallback is unaffected — it is not a network claim.
  const offlineAwareStatus = useOfflineAwareSyncStatus(status);

  const resolved: SyncBadgeStatus | undefined =
    offlineAwareStatus ?? (hasUndismissedNoteFailure ? 'note_failed' : undefined);

  // Nothing to show: the rollout is off, an ordinary session, or a live capture
  // (the recorder owns that affordance). Returning before the badge keeps
  // ordinary rows — the common case — from needing an `IntlProvider` just to
  // render nothing.
  if (!isOfflineRecordingEnabled || !resolved || resolved === 'recording') return null;

  // Dismissed-tick guard — rationale lives once, on `scheduleSyncedDismissal`
  // in `useOfflineRecordingEvents`.
  if (
    resolved === 'synced' &&
    Boolean(sessionId) &&
    dismissedSyncedIds.includes(sessionId!)
  )
    return null;

  return (
    <SyncStatusBadge
      status={resolved}
      hideLabel={hideLabel}
      className="shrink-0"
      data-testid={dataTestId}
    />
  );
};

OfflineSyncIndicator.displayName = 'OfflineSyncIndicator';
